import { useEffect, useRef } from 'react';

/**
 * Adds the `is-visible` class once the element enters the viewport.
 * Uses a single shared IntersectionObserver to keep runtime cost near zero.
 */
let observer: IntersectionObserver | null = null;

function getObserver(): IntersectionObserver | null {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return null;

  if (!observer) {
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer?.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  }

  return observer;
}

export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const instance = getObserver();
    if (!instance) {
      node.classList.add('is-visible');
      return;
    }

    instance.observe(node);
    return () => instance.unobserve(node);
  }, []);

  return ref;
}
